import { useCallback, useEffect, useRef, useState } from 'react';
import {
  decodeServerEnvelope,
  encodeCallNext,
  encodeFinishTicket,
} from '../services/protoService';
import { generateUUID } from '../utils/uuid';

const WS_BASE = import.meta.env.VITE_NETTY_WS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:8080`;

export function useDemoStaffQueue(shopId) {
  const [entries, setEntries] = useState([]);
  const [wsStatus, setWsStatus] = useState('disconnected');

  const wsRef = useRef(null);
  const activeRef = useRef(true);
  const retryRef = useRef(null);

  const connect = useCallback(() => {
    if (!shopId || !activeRef.current) return;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setWsStatus('connecting');
    const url = `${WS_BASE}/queue?shopId=${encodeURIComponent(shopId)}&role=demo-staff`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => setWsStatus('connected');

    ws.onmessage = async ({ data }) => {
      try {
        const env = await decodeServerEnvelope(data);
        if (env.queueUpdate) {
          setEntries(env.queueUpdate.entries || []);
        }
      } catch (e) {
        console.error('Failed to decode ServerEnvelope', e);
      }
    };

    ws.onclose = () => {
      if (!activeRef.current) return;
      setWsStatus('disconnected');
      retryRef.current = setTimeout(() => connect(), 3000);
    };

    ws.onerror = () => {};
  }, [shopId]);

  useEffect(() => {
    activeRef.current = true;
    connect();
    return () => {
      activeRef.current = false;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  const send = useCallback(async (encodeFn, params) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    try {
      const bytes = await encodeFn({ requestId: generateUUID(), ...params });
      wsRef.current.send(bytes);
    } catch (e) {
      console.error('Failed to encode message', e);
    }
  }, []);

  const callNext = useCallback(() => send(encodeCallNext, { shopId, serviceId: '' }), [send, shopId]);
  const finishTicket = useCallback((ticketUuid) => send(encodeFinishTicket, { shopId, ticketUuid, serviceId: '' }), [send, shopId]);

  return { entries, wsStatus, callNext, finishTicket };
}
